import type { StatementData } from '@/components/modal/PortfolioStatementModal';
import PortfolioStatement from '@/components/pdf/PortfolioStatement';
import { BalancesSummaryReturn } from '@/types/sso';
import { getCurrency } from '@/utils/hooks/useCurrency';
import { exportToPDF } from '@/utils/pdfExport';
import { BigNumber } from 'bignumber.js';

export const PORTFOLIO_ACCOUNT_TYPES = ['GB10', 'ManagedPortfolio'];

export const isPortfolioAccountType = (accountType?: string) => PORTFOLIO_ACCOUNT_TYPES.includes(accountType || '');

export type BalanceAccount = BalancesSummaryReturn['balances']['accountBased'][number];

export interface BuildPortfolioStatementDataParams {
    balances: BalancesSummaryReturn;
    account: Pick<BalanceAccount, 'accountName' | 'accountType' | 'totalAccountAmountInBaseCurrency'>;
    entityName: string;
    date: Date;
    locale: string;
    dateFormatter: Pick<Intl.DateTimeFormat, 'format'>;
    numberFormatter: Pick<Intl.NumberFormat, 'format'>;
}

export const checkSafeCurrency = (currency: string): boolean => {
    const validCurrencies: string[] = Intl.supportedValuesOf('currency');
    return validCurrencies.includes(currency?.toUpperCase());
};

export const getStatementNumberFormatOptions = (baseCurrency: string): Intl.NumberFormatOptions => {
    const options: Intl.NumberFormatOptions = {
        minimumFractionDigits: 0,
        maximumFractionDigits: getCurrency(baseCurrency)?.order_decimals || 2
    };
    const isSafeCurrency = checkSafeCurrency(baseCurrency);
    if (isSafeCurrency) {
        options.style = 'currency';
        options.currency = baseCurrency || 'USD';
    }

    return options;
};

export const statementDateFormatOptions: Intl.DateTimeFormatOptions = { timeZone: 'UTC' };

export const buildPortfolioStatementData = ({
    balances,
    account,
    entityName,
    date,
    locale,
    dateFormatter,
    numberFormatter
}: BuildPortfolioStatementDataParams): StatementData => {
    const { currencyBased } = balances.balances;
    const isAccountRow = (row: { accountType: string; accountName: string }) =>
        row.accountType === account.accountType && row.accountName === account.accountName;

    const allocationTotal = currencyBased.reduce((acc, item) => {
        return (
            acc +
            item.subRows.filter(isAccountRow).reduce((subAcc, row) => subAcc + row.totalAccountAmountInBaseCurrency, 0)
        );
    }, 0);

    return {
        metadata: {
            date: dateFormatter.format(date),
            entity: entityName,
            account: account.accountName,
            currency: balances.baseCurrency,
            balance: numberFormatter.format(account.totalAccountAmountInBaseCurrency ?? 0)
        },
        positions: currencyBased.map((item) => {
            const accountSubRows = item.subRows.filter(isAccountRow);

            const totalCurrencyAmount = accountSubRows.reduce((acc, row) => acc + row.totalAccountAmount, 0);
            const totalCurrencyAmountInBaseCurrency = accountSubRows.reduce(
                (acc, row) => acc + row.totalAccountAmountInBaseCurrency,
                0
            );

            const ccyConfig = getCurrency(item.currencyName);
            const totalFormatter = new Intl.NumberFormat(locale, {
                minimumFractionDigits: 0,
                maximumFractionDigits: ccyConfig?.order_decimals || 2
            });

            return {
                asset: item.currencyName,
                total: totalFormatter.format(totalCurrencyAmount),
                rate: numberFormatter.format(
                    BigNumber(totalCurrencyAmountInBaseCurrency)
                        .dividedBy(totalCurrencyAmount || 1)
                        .toNumber()
                ),
                live_value: numberFormatter.format(totalCurrencyAmountInBaseCurrency),
                allocation: BigNumber(totalCurrencyAmountInBaseCurrency)
                    .dividedBy(allocationTotal || 1)
                    .toNumber()
            };
        })
    };
};

export const exportPortfolioStatementPDF = async (statementData: StatementData, fileDate: string) => {
    await exportToPDF(
        <PortfolioStatement {...statementData} />,
        `${statementData.metadata.entity}_${statementData.metadata.account}_Statement_${fileDate}`
    );
};
